import { motion } from "framer-motion";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  FiCheckCircle,
  FiFileText,
  FiGlobe,
  FiInfo,
  FiSend,
  FiTag,
  FiType,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { categories } from "../../data/fakeData";

const SubmitWebsite = () => {
  const { user, openLogin } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    url: "",
    category: "",
    description: "",
    tags: "",
  });
  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  if (!user)
    return (
      <div className="section-tinted min-h-[70vh] flex items-center justify-center">
        <div className="text-center px-4 max-w-lg">
          <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-5">
            <FiGlobe size={28} className="text-indigo-600" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 mb-3">
            Submit Your Website
          </h1>
          <p className="text-slate-500 mb-6">
            Sign in to submit your website for community review.
          </p>
          <button
            onClick={openLogin}
            className="btn-primary text-base px-8 py-3"
          >
            Sign In to Submit
          </button>
        </div>
      </div>
    );

  if (submitted)
    return (
      <div className="section-tinted min-h-[70vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-4 max-w-lg"
        >
          <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <FiCheckCircle size={28} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 mb-3">
            Submitted Successfully!
          </h1>
          <p className="text-slate-500 mb-6">
            We'll review <strong className="text-slate-800">{form.name}</strong>{" "}
            within 2–3 business days and notify you by email.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setForm({
                name: "",
                url: "",
                category: "",
                description: "",
                tags: "",
              });
            }}
            className="btn-secondary"
          >
            Submit Another
          </button>
        </motion.div>
      </div>
    );

  return (
    <div className="section-tinted min-h-screen">
      <div className="container-main py-10 lg:py-14 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="accent-line" />
          <p className="label-tag mb-2">Share Your Site</p>
          <h1 className="page-title mb-2">Submit Your Website</h1>
          <p className="section-subtitle mb-8">
            All submissions are manually reviewed before being published to the
            directory.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={(e) => {
            e.preventDefault();
            toast.success("Submitted for review!");
            setSubmitted(true);
          }}
          className="card p-6 lg:p-8 space-y-5 shadow-lg shadow-slate-200/40"
        >
          {/* Fields */}
          {[
            {
              name: "name",
              label: "Website Name",
              type: "text",
              placeholder: "e.g., My Awesome Website",
              icon: FiType,
            },
            {
              name: "url",
              label: "Website URL",
              type: "url",
              placeholder: "https://example.com",
              icon: FiGlobe,
            },
          ].map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-bold text-slate-800 mb-1.5">
                {field.label}
              </label>
              <div className="relative">
                <field.icon
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type={field.type}
                  name={field.name}
                  value={form[field.name]}
                  onChange={onChange}
                  placeholder={field.placeholder}
                  className="input-field pl-10"
                  required
                />
              </div>
            </div>
          ))}

          <div>
            <label className="block text-sm font-bold text-slate-800 mb-1.5">
              Category
            </label>
            <select
              name="category"
              value={form.category}
              onChange={onChange}
              className="input-field appearance-none pr-9"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
              }}
              required
            >
              <option value="">Select a category...</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-800 mb-1.5">
              Description
            </label>
            <div className="relative">
              <FiFileText
                size={15}
                className="absolute left-3.5 top-3.5 text-slate-400"
              />
              <textarea
                name="description"
                value={form.description}
                onChange={onChange}
                placeholder="Describe your website clearly — what it does, who it's for, key features..."
                rows={4}
                className="input-field pl-10 resize-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-800 mb-1.5">
              Tags{" "}
              <span className="font-medium text-slate-400">(optional)</span>
            </label>
            <div className="relative">
              <FiTag
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                name="tags"
                value={form.tags}
                onChange={onChange}
                placeholder="e.g., productivity, saas, design (comma separated)"
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Guidelines */}
          <div className="bg-indigo-50 border-2 border-indigo-100 rounded-xl p-4">
            <div className="flex items-start gap-2.5">
              <FiInfo
                size={16}
                className="text-indigo-500 flex-shrink-0 mt-0.5"
              />
              <div>
                <p className="text-sm font-bold text-indigo-800 mb-2">
                  Submission Guidelines
                </p>
                <ul className="text-xs font-semibold text-indigo-700/80 space-y-1.5">
                  <li>• Website must be live and publicly accessible</li>
                  <li>• No adult, illegal, or spam content</li>
                  <li>• Provide an accurate and complete description</li>
                  <li>• Review process takes 2–3 business days</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Row */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2.5">
              <img
                src={user.avatar}
                alt=""
                className="w-8 h-8 rounded-full object-cover ring-2 ring-slate-100"
              />
              <div>
                <p className="text-sm font-bold text-slate-900">{user.name}</p>
                <p className="text-xs text-slate-400">{user.email}</p>
              </div>
            </div>
            <button type="submit" className="btn-primary px-6">
              <FiSend size={14} /> Submit for Review
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default SubmitWebsite;
